import g from '#core/global';
import events from '#core/events';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';


const is_active_attr = 'data-is-active';
const root_$ = ':root';
const slideshow_$ = '.slideshow';
const slideshow_items_$ = '.slideshow__slider-items';
const slideshow_item_$ = '.media-figure';
const slide_caption_$ = '.media-figure__caption';
const footer_$ = '.slideshow__footer';
const caption_text_$ = '.slideshow__caption-text';
const caption_index_$ = '.slideshow__caption-index p';
const caption_nav_$ = '.slideshow__caption-nav';
const lightbox_caption_$ = '.uk-lightbox.uk-open .uk-lightbox-caption';
const cursor_$ = '.slideshow__cursor';
const cursor_visible_attr = 'data-cursor-visible';
const active_index_attr = 'data-slideshow-active-index';
const lightbox_caption_height_prop = '--lightbox-caption-height';

// This must be larger than the caption transition time, as it serves as a
// fallback in case the transition event didn't fire correctly
const time_caption_transition_approx = 500;

const shrink_caption_height = false;

let has_slideshow = false;
let $slideshows = null;
let $root = null;
const has_been_jiggled = [];
const previous_height = {};
const lightbox_attr = 'data-lightbox';


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('slideshow')) return false;
		
		load.content(() => {
			has_slideshow = assign_elements();
			if (!has_slideshow) return false;
			
			assign_window_events();
		});
		load.all(() => {
			has_slideshow = assign_elements();
			if (!has_slideshow) return false;
			
			jiggle_slideshows();
		});
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_slideshow) return true;
	
	// TODO: do not store objects on load, account for dynamic content
	$slideshows = document.querySelectorAll(slideshow_$);
	if (!$slideshows.length) return false;
	
	$root = document.querySelector(root_$);
	
	$slideshows.forEach($slideshow => {
		const $cursor = $slideshow.querySelector(cursor_$);
		$slideshow.addEventListener(events.uikit.show_slide, e => {
			show_caption($slideshow, e);
		});
		$slideshow.addEventListener(g.mouse_move_e, e => {
			move_cursor($cursor, e);
		});
		$slideshow.querySelectorAll(slideshow_item_$).forEach($slide => {
			if (!$slide.hasAttribute(lightbox_attr)) return false;
			
			$slide.addEventListener(g.mouse_enter_e, e => {
				$slideshow.setAttribute(cursor_visible_attr, g.true_s);
			});
			$slide.addEventListener(g.mouse_leave_e, e => {
				$slideshow.setAttribute(cursor_visible_attr, g.false_s);
			});
		});
		show_caption($slideshow, null);
	});
	
	// Replace default UIkit lightbox arrows
	UIkit.util.on(document, events.uikit.show, '.uk-lightbox', function(){
		const $lightbox = document.querySelector('.uk-lightbox.uk-open');
		if (!$lightbox) return false;
		
		const $icon = $lightbox.querySelector('.uk-lightbox-button .tm-icon');
		if ($icon) return false;
		
		// This is the same as arrow.svg but with an added "tm-icon" class, and
		// the "vector-effect" attribute
		const arrow_svg = '<svg class="tm-icon" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g class="stroke"><path d="M 1 12 L 23 12 M 16 19.5 C 16 14, 20 12.75, 23 12 C 20 11.25, 16 10, 16 4.5" stroke-linejoin="bevel" vector-effect="non-scaling-stroke"/></g></svg>';
		
		const $nav_button = $lightbox.querySelectorAll('.uk-lightbox-button');
		$nav_button.forEach($button => {
			$button.innerHTML += arrow_svg;
		});
	});
	
	// Update lightbox caption height when switching slides
	UIkit.util.on(document, events.uikit.show_slide, '.uk-lightbox', function(){
		const $caption = document.querySelector(lightbox_caption_$);
		if (!$caption) return false;
		
		update_lightbox_caption_height();
	});
	
	// Show initial active slide
	setTimeout(() => {
		$slideshows.forEach($slideshow => {
			const $slider = $slideshow.querySelector(`[${active_index_attr}]`);
			const index = parseInt($slider.getAttribute(active_index_attr));
			if (index < 1) return false;
			
			UIkit.slider($slider).show(index);
		});
	}, 100);
	
	return true;
}


/**
 * Assign window events
 */
function assign_window_events() {
	window.addEventListener(g.scroll_e, util.throttle(() => {
		jiggle_slideshows();
	}, 150));
	
	window.addEventListener(g.resize_e, util.throttle(() => {
		update_all_heights(true);
		update_lightbox_caption_height();
	}, 150));
}


/**
 * Show caption
 * @param {element} $slideshow
 * @param {event} e
 */
function show_caption($slideshow, e) {
	const $footer = $slideshow.querySelector(footer_$);
	if (!$footer) return false;
	
	const is_caption_active = $footer.getAttribute(is_active_attr);
	if (is_caption_active === g.false_s) return false;
	
	const $slide = (!e)
		? $slideshow.querySelector(`${slideshow_items_$} > :first-child`)
		: e.target;
	const slide_index = Array.from($slide.parentNode.children).indexOf($slide);
	const slide_count = $slide.parentNode.children.length;
	const $slide_caption = $slide.querySelector(slide_caption_$);
	const caption_content = $slide_caption
		? $slide_caption.innerHTML
		: '';
	
	// After fading out current caption, show new caption
	const update_handler = () => {
		update_caption($footer, caption_content, slide_index, slide_count, update_handler);
	};
	$footer.addEventListener(g.transition_end_e, update_handler);
	
	// Fade out current caption
	$footer.setAttribute(is_active_attr, g.false_s);
	
	// If the transition event doesn't fire correctly, manually check to see if
	// we need to update the caption
	setTimeout(() => {
		if ($footer.getAttribute(is_active_attr) === g.true_s) return false;
		
		update_caption($footer, caption_content, slide_index, slide_count, update_handler);
	}, time_caption_transition_approx);
}


/**
 * Update caption
 * @param {element} $footer
 * @param {string} caption_content
 * @param {int} slide_index
 * @param {int} slide_count
 * @param {function} callback
 */
function update_caption($footer, caption_content, slide_index, slide_count, callback) {
	const $inner = $footer.querySelector(caption_text_$);
	const $index = $footer.querySelector(caption_index_$);
	if (!$inner || !$index) return false;
	
	$inner.innerHTML = caption_content;
	$index.innerHTML = `${slide_index + 1}/${slide_count}`;
	update_slideshow_height($footer);
	$footer.removeEventListener(g.transition_end_e, callback);
	$footer.setAttribute(is_active_attr, g.true_s);
}


/**
 * Update all slideshow heights
 * @param {boolean} reset
 */
function update_all_heights(reset = false) {
	$slideshows.forEach($slideshow => {
		const $footer = $slideshow.querySelector(footer_$);
		if (!$footer) return false;
		
		update_slideshow_height($footer, reset);
	});
}


/**
 * Update slideshow height as height of caption changes
 * @param {element} $footer
 * @param {boolean} reset
 */
function update_slideshow_height($footer, reset = false) {
	const slideshow_id = $footer.closest(slideshow_$).id;
	const $index = $footer.querySelector(caption_index_$);
	const $text = $footer.querySelector(caption_text_$);
	if (!$index || !$text) return false;
	
	const $nav = $footer.querySelector(caption_nav_$);
	
	const index_top = $index.getBoundingClientRect().top;
	// const text_top = $text.getBoundingClientRect().top;
	const nav_top = $nav.getBoundingClientRect().top;
	const top_threshold = 10;
	
	if (reset) {
		previous_height[slideshow_id] = 0;
	}
	
	let height = 0;
	if (
		Math.abs(index_top - nav_top) < top_threshold
	) {
		height = Math.ceil(Math.max(
			layout.getHeight($index),
			layout.getHeight($text),
			layout.getHeight($nav)
		));
	} else {
		height = Math.ceil(
			layout.getHeight($index)
			+ layout.getHeight($text)
			+ layout.getHeight($nav)
		);
	}
	
	if (
		height !== 0
		&& (
			shrink_caption_height
			|| height > (previous_height[slideshow_id] || 0)
		)
	) {
		layout.setHeight($footer, height);
		previous_height[slideshow_id] = height;
	}
}


/**
 * Jiggle slideshow widths as they come into view
 */
function jiggle_slideshows() {
	$slideshows.forEach($slideshow => {
		if (has_been_jiggled.includes($slideshow.id)) return false;
		
		const $slider_items = $slideshow.querySelector(slideshow_items_$);
		const is_visible = layout.isIntersecting({
			$element: $slider_items,
			lookaround: 1,
		});
		if (!is_visible) return false;
		
		has_been_jiggled.push($slideshow.id);
		jiggle_width($slideshow);
	});
}


/**
 * Jiggle the width of the slideshow to circumvent an issue where the slideshow
 * is not properly initialized until the width of the container changes
 * @param {element} $slideshow
 */
function jiggle_width($slideshow) {
	const width = layout.getWidth($slideshow, false);
	layout.setWidth($slideshow, width - 2);
	setTimeout(() => {
		layout.setWidth($slideshow, '');
	}, 200);
}


/**
 * Move cursor
 * @param {element} $cursor
 * @param {event} e
 */
function move_cursor($cursor, e) {
	const x = e.clientX;
	const y = e.clientY;
	$cursor.style.left = `${x}px`;
	$cursor.style.top = `${y}px`;
}


/**
 * Get height of lightbox caption
 * @return {int}
 */
function get_lightbox_caption_height() {
	const $caption = document.querySelector(lightbox_caption_$);
	if (!$caption) return false;
	
	return Math.floor(layout.getHeight($caption, false));
}


/**
 * Update height of lightbox caption
 */
function update_lightbox_caption_height() {
	const height = get_lightbox_caption_height();
	if (!height) return false;
	
	$root.style.setProperty(lightbox_caption_height_prop, `${height}px`);
}
